// Client mirror of supabase/functions/_shared/hire-billing-period.ts (the app
// does not import across into supabase/functions; parity is enforced by the deno
// test in that directory).
//
// SEQ-169: Install & Hire billing arithmetic, discount-aware.
//
// A variation can carry claims_variations.discount_percent (percent OFF the
// agreed price). Works lines have always applied it; hire lines never did, so
// hire on a discounted variation was billed at the full undiscounted rate.
//
// The discount has to land INSIDE calculateHireBillingPeriod, not downstream of
// it: generate-progress-claim writes the recalculated amount straight back to
// claims_hire_billing.amount for every unpaid row, so a discount applied at the
// sync or register layer alone is reverted on the next claim generation.
//
// calculateHireBillingPeriod was previously duplicated byte-identically in
// generate-progress-claim/index.ts and src/components/ProgressClaimModal.tsx.
// The modal's preview and the generated PDF must agree to the cent, so this
// file and the edge module have to stay in lockstep.

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const DATE_MONTH_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function round2(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

export function parseDateOnlyUtc(value?: string | null): number | null {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return null;
  const [, year, month, day] = match;
  return Date.UTC(Number(year), Number(month) - 1, Number(day));
}

function formatDateOnlyUtcLabel(value: number): string {
  const date = new Date(value);
  return `${date.getUTCDate()} ${DATE_MONTH_SHORT[date.getUTCMonth()]} ${date.getUTCFullYear()}`;
}

export function formatMonthOnlyLabel(billingMonth: number, billingYear: number): string {
  return `${DATE_MONTH_SHORT[billingMonth - 1]} ${billingYear}`;
}

/**
 * Fraction of the agreed hire price still payable after the parent variation's
 * discount. Clamped to [0, 1]: claims_variations.discount_percent is CHECK
 * constrained to 0..100, and a null / undefined / non-numeric value means no
 * discount rather than a thrown claim. 100 percent returns exactly 0, so a
 * fully discounted line can never bill a negative amount.
 */
export function hireDiscountMultiplier(discountPercent?: number | null): number {
  const percent = Number(discountPercent ?? 0);
  if (!Number.isFinite(percent) || percent <= 0) return 1;
  if (percent >= 100) return 0;
  return 1 - percent / 100;
}

/** Round-to-cents discount application, for amounts not built by this module. */
export function applyHireDiscount(
  amount: number,
  discountPercent?: number | null,
): number {
  return round2(amount * hireDiscountMultiplier(discountPercent));
}

export interface HireBillingPeriodParams {
  billingMonth: number;
  billingYear: number;
  startDate?: string | null;
  endDate?: string | null;
  costPerWeek: number;
  qty: number;
  levels: number;
  fallbackWeeks: number;
  fallbackAmount: number;
  /** claims_variations.discount_percent of the register row's parent variation. */
  discountPercent?: number | null;
}

export interface HireBillingPeriodResult {
  weeksInPeriod: number;
  amount: number;
  periodStartDate: string | null;
  periodEndDate: string | null;
  periodLabel: string;
}

export function calculateHireBillingPeriod(
  params: HireBillingPeriodParams,
): HireBillingPeriodResult {
  const startUtc = parseDateOnlyUtc(params.startDate);
  if (
    startUtc === null ||
    params.costPerWeek <= 0 ||
    params.qty <= 0 ||
    params.levels <= 0
  ) {
    // The fallbacks are the values already stored on claims_hire_billing, which
    // sync-variations writes discounted. Applying the discount again here would
    // double-discount a row every time a claim is regenerated, so this branch
    // must pass them through untouched.
    return {
      weeksInPeriod: params.fallbackWeeks,
      amount: params.fallbackAmount,
      periodStartDate: null,
      periodEndDate: null,
      periodLabel: formatMonthOnlyLabel(params.billingMonth, params.billingYear),
    };
  }

  const periodStartUtc = Date.UTC(params.billingYear, params.billingMonth - 1, 1);
  const periodEndUtc = Date.UTC(params.billingYear, params.billingMonth, 0);
  const endUtc = parseDateOnlyUtc(params.endDate) ?? periodEndUtc;
  const overlapStartUtc = Math.max(periodStartUtc, startUtc);
  const overlapEndUtc = Math.min(periodEndUtc, endUtc);

  // SEQ-218: a day is billed when start < day <= end, the same count Dashpivot
  // and the register total use ((end - start) / 7). The start day is not
  // billed, so the first billed day of a month is the later of the day after
  // the hire start and the 1st. Counting both ends billed one extra day on
  // every closed hire.
  const billedFromUtc = Math.max(periodStartUtc - MS_PER_DAY, startUtc);
  const daysInOverlap = Math.floor((overlapEndUtc - billedFromUtc) / MS_PER_DAY);

  if (overlapEndUtc < overlapStartUtc || daysInOverlap <= 0) {
    return {
      weeksInPeriod: 0,
      amount: 0,
      periodStartDate: null,
      periodEndDate: null,
      periodLabel: formatMonthOnlyLabel(params.billingMonth, params.billingYear),
    };
  }

  const rawWeeks = daysInOverlap / 7;

  return {
    weeksInPeriod: Math.round((rawWeeks + Number.EPSILON) * 100) / 100,
    // The multiplier is folded in before rounding, so a zero-discount line
    // rounds to exactly the cent it did before SEQ-169.
    amount: round2(
      rawWeeks *
        params.costPerWeek *
        params.qty *
        params.levels *
        hireDiscountMultiplier(params.discountPercent),
    ),
    periodStartDate: new Date(overlapStartUtc).toISOString().slice(0, 10),
    periodEndDate: new Date(overlapEndUtc).toISOString().slice(0, 10),
    periodLabel: `${formatDateOnlyUtcLabel(overlapStartUtc)} - ${formatDateOnlyUtcLabel(overlapEndUtc)}`,
  };
}

/**
 * discount_percent off a PostgREST `claims_variations` embed hanging under a
 * `claims_hire_register` embed. PostgREST returns an embed as an object or as a
 * single-element array depending on how it infers the relationship, and the
 * codebase already normalises both shapes elsewhere, so both are read here.
 * A missing parent means no discount, never a thrown claim.
 */
export function embeddedVariationDiscountPercent(value: unknown): number {
  const candidate = Array.isArray(value) ? value[0] : value;
  if (!candidate || typeof candidate !== "object") return 0;
  const percent = (candidate as { discount_percent?: number | null })
    .discount_percent;
  const parsed = Number(percent ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

// ─── sync-variations: stored register + billing amounts ─────────────────────
// sync-variations regenerates claims_hire_register.total_amount and every
// claims_hire_billing.amount from the Dashpivot hire item on each run. Without
// the discount here, every sync re-inflates stored rows back to full price.

export interface HireRegisterTotalParams {
  manualCost: number;
  costPerWeek: number;
  qty: number;
  levels: number;
  effectiveWeeks: number;
  discountPercent?: number | null;
}

/**
 * Register forecast total. manual_cost is a lump-sum agreed hire price, so it
 * carries the discount too: a 100 percent discounted manual line is worth 0.
 */
export function hireRegisterTotalAmount(params: HireRegisterTotalParams): number {
  const gross = params.manualCost > 0
    ? params.manualCost
    : params.costPerWeek * params.qty * params.levels * params.effectiveWeeks;
  return round2(gross * hireDiscountMultiplier(params.discountPercent));
}

export interface HireMonthlyAmountParams {
  weeksInPeriod: number;
  costPerWeek: number;
  qty: number;
  levels: number;
  discountPercent?: number | null;
}

export function hireMonthlyBillingAmount(params: HireMonthlyAmountParams): number {
  return round2(
    params.weeksInPeriod *
      params.costPerWeek *
      params.qty *
      params.levels *
      hireDiscountMultiplier(params.discountPercent),
  );
}

/** The claims_hire_billing payload sync-variations regenerates per month. */
export interface HireBillingUpsertRecord {
  hire_register_id: string;
  project_id: string;
  organization_id: string;
  billing_month: number;
  billing_year: number;
  weeks_in_period: number;
  amount: number;
  sub_docket_no: string;
  sub_sequence: number;
  billing_status: string;
}

export interface StoredHireBillingMonth {
  billing_month: number;
  billing_year: number;
  paid?: boolean | null;
  billing_status?: string | null;
  claim_report_id?: string | null;
}

/**
 * Months sync-variations may rewrite. The billing upsert conflicts on
 * (hire_register_id, billing_month, billing_year) with ignoreDuplicates false,
 * so it overwrites amount, weeks_in_period and billing_status on whatever is
 * already there. Before SEQ-169 that only re-derived the same figure, but now
 * that the amount depends on a discount, a discount added later would silently
 * re-price money that has already left the building.
 *
 * Two states are off limits, not one. A PAID month is settled. An ISSUED month
 * (attached to a claim report, awaiting payment) is a figure the builder has
 * already been invoiced: re-pricing it makes the stored amount disagree with a
 * PDF that is out in the world, and it drags sumHireBilledToDate, and so page 1
 * line A, along with it.
 *
 * The issued test is `claim_report_id != null`, deliberately, and not the other
 * two candidates:
 *   - billing_status is unusable, because sync's own upsert writes
 *     billing_status 'pending' onto every month it rewrites. A month claimed in
 *     June reads 'pending' again after the next sync, so a status test would
 *     pass exactly when it matters least.
 *   - included_in_claim is a denormalised restatement of the same fact. Every
 *     site that sets one sets the other (generate-progress-claim's inclusion
 *     update, its stale-inclusion reset, releaseHireRowsForReport, and the
 *     payment RPC), so it adds no coverage, and a stale boolean can outlive its
 *     report where an FK pointer cannot.
 * claim_report_id is also the field isHireRowClaimable keys off for its
 * foreign-claim decision, so sync's guard and the claim engine agree on what
 * "attached to a claim" means rather than each having a private opinion.
 *
 * Releasing is the escape hatch: deleting a claim nulls claim_report_id, so the
 * month becomes re-priceable again. Regenerating the claim re-prices it and
 * reissues the PDF, which is the only way the two should ever move together.
 */
export function selectResyncableHireMonths<
  T extends { billing_month: number; billing_year: number },
>(
  generated: readonly T[],
  storedRows: readonly StoredHireBillingMonth[],
): T[] {
  const settledKeys = new Set<string>();
  for (const row of storedRows) {
    const isPaid = Boolean(row.paid) || row.billing_status === "paid";
    const isIssued = Boolean(row.claim_report_id);
    if (isPaid || isIssued) {
      settledKeys.add(`${Number(row.billing_year)}-${Number(row.billing_month)}`);
    }
  }
  return generated.filter(
    (record) =>
      !settledKeys.has(
        `${Number(record.billing_year)}-${Number(record.billing_month)}`,
      ),
  );
}

/**
 * Which stored hire months may be DELETED as stale, given the months the
 * register now generates.
 *
 * Sibling of selectResyncableHireMonths above, and it answers the same
 * question about the same rows: is this month settled. It exists because the
 * two were NOT asking it the same way. The re-pricing guard tests
 * `claim_report_id != null`, deliberately, for the reason written out above:
 * sync's own upsert rewrites billing_status to 'pending' on every month it
 * touches, so a month claimed in June reads 'pending' again after the next
 * sync. The delete guard in sync-variations tested ONLY that same unusable
 * billing_status, and its SELECT did not even fetch claim_report_id, so a row
 * attached to a live claim could be deleted while being protected from
 * re-pricing. The builder has already been invoiced for that line.
 *
 * The settled test here is deliberately the UNION of both: paid, attached to a
 * claim, or carrying a status other than pending. That is strictly more
 * conservative than the old behaviour, so this function can only ever delete
 * FEWER rows than the code it replaces, never more.
 */
export function selectDeletableHireMonths<
  T extends StoredHireBillingMonth & { id: string },
>(
  storedRows: readonly T[],
  validMonthKeys: readonly string[],
): T[] {
  const valid = new Set(validMonthKeys.map((k) => String(k)));
  return storedRows.filter((row) => {
    const key = `${Number(row.billing_year)}-${String(Number(row.billing_month)).padStart(2, "0")}`;
    if (valid.has(key)) return false;
    const isPaid = Boolean(row.paid) || row.billing_status === "paid";
    const isIssued = Boolean(row.claim_report_id);
    const isPending = !row.billing_status || row.billing_status === "pending";
    return !isPaid && !isIssued && isPending;
  });
}

// ─── Hire register screen: client-side live totals ──────────────────────────
// HireRegisterSection recomputes an open-ended item's running total at render
// time instead of reading the stored forecast, so it needs the discount too or
// the screen keeps quoting the undiscounted rate after everything else is fixed.

export interface HireWeeklyRateParams {
  costPerWeek: number;
  qty: number;
  levels: number;
  discountPercent?: number | null;
}

export function hireWeeklyRate(params: HireWeeklyRateParams): number {
  return (
    params.costPerWeek *
    params.qty *
    params.levels *
    hireDiscountMultiplier(params.discountPercent)
  );
}

export interface HireDisplayTotalParams {
  costPerWeek: number;
  qty: number;
  levels: number;
  manualCost: number;
  /** claims_hire_register.total_amount, already discounted by sync. */
  storedTotalAmount: number;
  /** Sum of the stored claims_hire_billing amounts, already discounted. */
  billingTotal: number;
  /** Weeks from start to now for an open-ended active item, otherwise null. */
  runningWeeks: number | null;
  discountPercent?: number | null;
}

function hireLiveTotalAmount(params: HireDisplayTotalParams): number {
  // The "is a rate configured" test stays on the raw rate: a fully discounted
  // line still has a rate, it is just worth nothing, and must not fall through
  // to the undiscounted manual_cost branch.
  const hasRate = params.costPerWeek * params.qty * params.levels > 0;
  const weeklyRate = hireWeeklyRate(params);
  const discountedManualCost = applyHireDiscount(
    params.manualCost,
    params.discountPercent,
  );

  if (params.runningWeeks !== null) {
    if (hasRate) return round2(weeklyRate * params.runningWeeks);
    if (params.manualCost > 0) return discountedManualCost;
    return params.storedTotalAmount;
  }
  if (params.storedTotalAmount > 0) return params.storedTotalAmount;
  if (params.manualCost > 0 && !hasRate) return discountedManualCost;
  return params.storedTotalAmount;
}

export function hireDisplayTotalAmount(params: HireDisplayTotalParams): number {
  return Math.max(hireLiveTotalAmount(params), params.billingTotal);
}
